<script setup lang="ts">
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue';
import { useRouter } from 'vue-router';

import { useStepper } from '@vueuse/core';
import type { AnyMaskedOptions } from 'imask';

import { useBankTransfer } from '@/composables/useBankTransfer';
import { useErrorModal } from '@/composables/useErrorModal';
import { placeholderBackgroundColor } from '@/helpers/colors';
import { isTouchDevice } from '@/helpers/device';
import { formatIBAN, getInitials } from '@/helpers/formatters';
import { fade, type MotionVariants, slideLeft, slideRight } from '@/helpers/motion';
import { BankTransferRecipientType } from '@/references/axios/bankTransfer/types';
import { useI18n } from '@/references/i18n';
import { RouteNames } from '@/router';

import { InnerLayoutButtonType } from '@/components/layouts/InnerLayout.types';
import InnerLayout from '@/components/layouts/InnerLayout.vue';
import TransactionReview from '@/components/TransactionReview.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiColoredCircle from '@/components/ui/UiColoredCircle.vue';
import UiInput from '@/components/ui/UiInput.vue';
import type { UiListPairGroup } from '@/components/ui/UiListPair.types';
import UiProgressLine from '@/components/ui/UiProgressLine.vue';
import UiText from '@/components/ui/UiText.vue';
import UiTitle from '@/components/ui/UiTitle.vue';

const NAME_REG = /^[0-9a-zA-Z ]+$/;
const IBAN_REG = /^[0-9a-zA-Z ]+$/;

enum BankTransferBeneficiaryFormViewStep {
  FirstName = 'first-name',
  LastName = 'last-name',
  CompanyName = 'company-name',
  Iban = 'iban',
  Nickname = 'nickname',
  Review = 'review'
}

type BankTransferBeneficiaryFormViewProgressStep = {
  steps: BankTransferBeneficiaryFormViewStep[];
  currentValue: number;
  potentialValue: number;
};

type BankTransferBeneficiaryFormViewFormItem = {
  step: BankTransferBeneficiaryFormViewStep;
  title: string;
  description: string;
  placeholder: string;
  error: string;
  mask?: AnyMaskedOptions;
  optional?: boolean;
  value: string;
  validate: (value: string) => boolean;
  handle: (value: string) => void;
  blur: (value: string) => void;
};

interface Props {
  id?: string;
  type?: BankTransferRecipientType;
}

const props = defineProps<Props>();

const state = reactive<{
  isMounted: boolean;
  previousStep: BankTransferBeneficiaryFormViewStep | null;
  firstName: string;
  lastName: string;
  companyName: string;
  iban: string;
  nickname: string;
  error: string;
  serverData?: {
    iban: string;
    bic: string;
    bankName: string;
  };
  isCheckIbanLoading: boolean;
  isSubmitLoading: boolean;
}>({
  isMounted: false,
  previousStep: null,
  firstName: '',
  lastName: '',
  companyName: '',
  iban: '',
  nickname: '',
  error: '',
  serverData: undefined,
  isCheckIbanLoading: false,
  isSubmitLoading: false
});

const inputComponent = ref<typeof UiInput>();

const { t } = useI18n();
const { replace } = useRouter();
const { recipients, checkIban, editRecipient, createRecipient } = useBankTransfer();
const toast = useErrorModal();

const isCreating = computed((): boolean => !!props.type);

const isIndividual = computed((): boolean => {
  const computedType = isCreating.value
    ? props.type
    : recipients.value.find((item) => item.id === Number(props.id))?.type;

  return computedType === BankTransferRecipientType.Individual;
});

const stepper = useStepper(
  isIndividual.value
    ? [
        BankTransferBeneficiaryFormViewStep.FirstName,
        BankTransferBeneficiaryFormViewStep.LastName,
        BankTransferBeneficiaryFormViewStep.Iban,
        BankTransferBeneficiaryFormViewStep.Nickname,
        BankTransferBeneficiaryFormViewStep.Review
      ]
    : [
        BankTransferBeneficiaryFormViewStep.CompanyName,
        BankTransferBeneficiaryFormViewStep.Iban,
        BankTransferBeneficiaryFormViewStep.Nickname,
        BankTransferBeneficiaryFormViewStep.Review
      ]
);

const PROGRESS_STEP_PERCENTS: BankTransferBeneficiaryFormViewProgressStep[] = [
  {
    steps: [
      BankTransferBeneficiaryFormViewStep.FirstName,
      BankTransferBeneficiaryFormViewStep.CompanyName
    ],
    currentValue: 16,
    potentialValue: 33
  },
  {
    steps: [BankTransferBeneficiaryFormViewStep.LastName],
    currentValue: 25,
    potentialValue: 50
  },
  {
    steps: [BankTransferBeneficiaryFormViewStep.Iban],
    currentValue: 50,
    potentialValue: 75
  },
  {
    steps: [BankTransferBeneficiaryFormViewStep.Nickname],
    currentValue: 75,
    potentialValue: 87
  },
  {
    steps: [BankTransferBeneficiaryFormViewStep.Review],
    currentValue: 87,
    potentialValue: 100
  }
];

const currentProgressStep = computed(
  (): BankTransferBeneficiaryFormViewProgressStep =>
    PROGRESS_STEP_PERCENTS.find((item) => item.steps.includes(stepper.current.value)) ??
    PROGRESS_STEP_PERCENTS[0]
);

const formSteps = computed((): BankTransferBeneficiaryFormViewFormItem[] => [
  ...(isIndividual.value
    ? [
        {
          step: BankTransferBeneficiaryFormViewStep.FirstName,
          title: t('createBeneficiary.firstName.title'),
          description: t('createBeneficiary.firstName.description'),
          placeholder: t('createBeneficiary.firstName.placeholder'),
          error: t('errors.invalidFirstName'),
          mask: { mask: NAME_REG, maxLength: 90 },
          value: state.firstName,
          validate: (value: string) => validateName(value),
          handle: (value: string) => {
            clearError();
            state.firstName = value;
          },
          blur: () => {
            state.firstName = state.firstName.trim();
          }
        },
        {
          step: BankTransferBeneficiaryFormViewStep.LastName,
          title: t('createBeneficiary.lastName.title'),
          description: t('createBeneficiary.lastName.description', {
            firstName: state.firstName.toUpperCase()
          }),
          placeholder: t('createBeneficiary.lastName.placeholder'),
          error: t('errors.invalidLastName'),
          mask: { mask: NAME_REG, maxLength: 90 },
          value: state.lastName,
          validate: (value: string) => validateName(value),
          handle: (value: string) => {
            clearError();
            state.lastName = value;
          },
          blur: () => {
            state.lastName = state.lastName.trim();
          }
        }
      ]
    : [
        {
          step: BankTransferBeneficiaryFormViewStep.CompanyName,
          title: t('createBeneficiary.companyName.title'),
          description: t('createBeneficiary.companyName.description'),
          placeholder: t('createBeneficiary.companyName.placeholder'),
          error: t('errors.invalidCompanyName'),
          mask: { mask: NAME_REG, maxLength: 90 },
          value: state.companyName,
          validate: (value: string) => validateName(value),
          handle: (value: string) => {
            clearError();
            state.companyName = value;
          },
          blur: () => {
            state.companyName = state.companyName.trim();
          }
        }
      ]),
  {
    step: BankTransferBeneficiaryFormViewStep.Iban,
    title: t('createBeneficiary.iban.title'),
    description: t('createBeneficiary.iban.description', {
      fullName: (isIndividual.value
        ? `${state.firstName} ${state.lastName}`
        : state.companyName
      ).toUpperCase()
    }),
    placeholder: '**** **** **** **** **** ****',
    error: t('errors.invalidIBAN'),
    mask: {
      mask: 'ssss ssss ssss ssss ssss ssss ssss ssss ss',
      definitions: {
        s: IBAN_REG
      }
    },
    value: state.iban,
    validate: (value: string) => validateIban(value),
    handle: (value: string) => {
      clearError();
      state.iban = value;
    },
    blur: () => {
      state.iban = state.iban.trim();
    }
  },
  {
    step: BankTransferBeneficiaryFormViewStep.Nickname,
    title: t('createBeneficiary.nickName.title'),
    description: t('createBeneficiary.nickName.description'),
    placeholder: t('createBeneficiary.nickName.placeholder'),
    error: t('errors.invalidNickname'),
    value: state.nickname,
    optional: true,
    mask: { mask: '****************************************' },
    validate: () => true,
    handle: (value: string) => {
      clearError();
      state.nickname = value;
    },
    blur: () => {
      state.nickname = state.nickname.trim();
    }
  }
]);

const currentFormStep = computed((): BankTransferBeneficiaryFormViewFormItem | undefined =>
  formSteps.value.find((item) => item.step === stepper.current.value)
);

const tipTitle = computed((): string => {
  switch (stepper.current.value) {
    case BankTransferBeneficiaryFormViewStep.CompanyName:
      return t('createBeneficiary.companyName.modal.title');
    case BankTransferBeneficiaryFormViewStep.FirstName:
      return t('createBeneficiary.firstName.modal.title');
    case BankTransferBeneficiaryFormViewStep.LastName:
      return t('createBeneficiary.lastName.modal.title');
    case BankTransferBeneficiaryFormViewStep.Iban:
      return t('createBeneficiary.iban.modal.title');
    case BankTransferBeneficiaryFormViewStep.Nickname:
      return t('createBeneficiary.nickName.modal.title');
    case BankTransferBeneficiaryFormViewStep.Review:
      return t('createBeneficiary.review.modal.title');
    default:
      return '';
  }
});

const tipDescription = computed((): string => {
  switch (stepper.current.value) {
    case BankTransferBeneficiaryFormViewStep.CompanyName:
      return t('createBeneficiary.companyName.modal.description');
    case BankTransferBeneficiaryFormViewStep.FirstName:
      return t('createBeneficiary.firstName.modal.description');
    case BankTransferBeneficiaryFormViewStep.LastName:
      return t('createBeneficiary.lastName.modal.description');
    case BankTransferBeneficiaryFormViewStep.Iban:
      return t('createBeneficiary.iban.modal.description');
    case BankTransferBeneficiaryFormViewStep.Nickname:
      return t('createBeneficiary.nickName.modal.description');
    case BankTransferBeneficiaryFormViewStep.Review:
      return t('createBeneficiary.review.modal.description');
    default:
      return '';
  }
});

const internalMotion = computed((): MotionVariants<never> | undefined => {
  if (!state.isMounted) {
    return fade;
  }

  if (!state.previousStep || stepper.isAfter(state.previousStep)) {
    return slideRight;
  }

  return slideLeft;
});

const isInputDisabled = computed(
  (): boolean =>
    stepper.current.value === BankTransferBeneficiaryFormViewStep.Iban && state.isCheckIbanLoading
);

const isFormButtonLoading = computed(
  (): boolean =>
    stepper.current.value === BankTransferBeneficiaryFormViewStep.Iban && state.isCheckIbanLoading
);

const isFormButtonDisabled = computed((): boolean => {
  const value = currentFormStep.value?.value ?? '';

  if (currentFormStep.value?.optional && !value.trim()) {
    return true;
  }

  return false;
});

const name = computed((): string => {
  if (state.nickname) {
    return state.nickname;
  }

  if (isIndividual.value) {
    return `${state.firstName} ${state.lastName}`.toUpperCase();
  }

  return state.companyName.toUpperCase();
});

const formattedIBAN = computed(() => formatIBAN(state.serverData?.iban ?? ''));

const details = computed((): UiListPairGroup[] => [
  {
    items: [
      ...(isIndividual.value
        ? [
            {
              label: t('createBeneficiary.review.items.firstName'),
              value: state.firstName.toUpperCase()
            },
            {
              label: t('createBeneficiary.review.items.lastName'),
              value: state.lastName.toUpperCase()
            }
          ]
        : [
            {
              label: t('createBeneficiary.review.items.companyName'),
              value: state.companyName.toUpperCase()
            }
          ]),
      {
        label: t('createBeneficiary.review.items.iban'),
        value: formattedIBAN.value
      },
      ...(state.nickname
        ? [
            {
              label: t('createBeneficiary.review.items.nickname'),
              value: state.nickname
            }
          ]
        : []),
      {
        label: t('createBeneficiary.review.items.bic'),
        value: state.serverData?.bic
      },
      {
        label: t('createBeneficiary.review.items.bank'),
        value: state.serverData?.bankName
      }
    ]
  }
]);

const reviewButtonText = computed((): string => {
  if (isCreating.value) {
    return t('buttons.addNewRecipient');
  }

  return t('buttons.updateTheRecipient');
});

const skipText = computed((): string => {
  if (isCreating.value) {
    return t('buttons.skip');
  }

  const currentRecipient = recipients.value.find((item) => item.id === Number(props.id));

  return currentRecipient?.nickname ? t('buttons.clear') : t('buttons.skip');
});

function validateName(value: string): boolean {
  return !!value.trim() && NAME_REG.test(value);
}

function validateIban(value: string): boolean {
  return !!value.trim() && IBAN_REG.test(value);
}

function goBack(): void {
  if (
    [
      BankTransferBeneficiaryFormViewStep.CompanyName,
      BankTransferBeneficiaryFormViewStep.FirstName
    ].includes(stepper.current.value)
  ) {
    replace({ name: RouteNames.Home });
    return;
  }

  if (stepper.current.value === BankTransferBeneficiaryFormViewStep.Iban) {
    state.serverData = undefined;
  }

  state.previousStep = stepper.current.value;
  stepper.goToPrevious();
}

function skip(): void {
  if (currentFormStep.value) {
    currentFormStep.value.handle('');
  }
  state.previousStep = stepper.current.value;
  stepper.goToNext();
}

async function goNext(): Promise<void> {
  if (currentFormStep.value && !currentFormStep.value?.validate(currentFormStep.value.value)) {
    if (state.error) {
      inputComponent.value?.shake();
    }
    state.error = currentFormStep.value?.error;
    inputComponent.value?.focus();
    return;
  }

  if (currentFormStep.value?.step !== BankTransferBeneficiaryFormViewStep.Iban) {
    state.previousStep = stepper.current.value;
    stepper.goToNext();
    return;
  }

  try {
    state.isCheckIbanLoading = true;

    const { isIBANValid, isCountryValid, iban, bic, bankName } = await checkIban(
      state.iban.replace(/\s/g, '')
    );

    if (!isIBANValid) {
      if (state.error) {
        inputComponent.value?.shake();
      }
      state.error = t('errors.invalidIBAN');
      return;
    }

    if (!isCountryValid) {
      if (state.error) {
        inputComponent.value?.shake();
      }
      state.error = t('errors.invalidCountryCode');
      return;
    }

    state.serverData = { iban, bic, bankName };
    state.previousStep = stepper.current.value;
    stepper.goToNext();
  } finally {
    state.isCheckIbanLoading = false;
  }
}

function clearError(): void {
  state.error = '';
}

function init(): void {
  if (
    [BankTransferRecipientType.Business, BankTransferRecipientType.Individual].includes(
      props.type as BankTransferRecipientType
    )
  ) {
    return;
  }

  const currentRecipient = recipients.value.find((item) => item.id === Number(props.id));

  if (currentRecipient) {
    state.companyName = currentRecipient.firstName;
    state.firstName = currentRecipient.firstName;
    state.lastName = currentRecipient.lastName;
    state.iban = currentRecipient.iban;
    state.nickname = currentRecipient.nickname;
    return;
  }

  replace({ name: RouteNames.Home });
}

function focus(): void {
  inputComponent.value?.focus();
}

function onChangeInputRef(item: Element | ComponentPublicInstance | null): void {
  inputComponent.value = item as unknown as typeof UiInput;
}

async function onSubmit(): Promise<void> {
  if (!state.serverData) {
    return;
  }

  try {
    if (isCreating.value) {
      if (!props.type) {
        return;
      }

      state.isSubmitLoading = true;
      await createRecipient(
        props.type,
        isIndividual.value ? state.firstName.trim() : state.companyName.trim(),
        state.lastName.trim(),
        state.nickname.trim(),
        state.serverData?.iban,
        state.serverData?.bic,
        state.serverData?.bankName
      );
      toast.auto(t('toasts.beneficiaryAdded'));
      replace({ name: RouteNames.Home });
    } else {
      if (!props.id) {
        return;
      }

      state.isSubmitLoading = true;
      await editRecipient(
        Number(props.id),
        isIndividual.value
          ? BankTransferRecipientType.Individual
          : BankTransferRecipientType.Business,
        isIndividual.value ? state.firstName.trim() : state.companyName.trim(),
        state.lastName.trim(),
        state.nickname.trim(),
        state.serverData?.iban,
        state.serverData?.bic,
        state.serverData?.bankName
      );
      toast.auto(t('toasts.beneficiaryEdited'));
      replace({ name: RouteNames.Home });
    }
  } finally {
    state.isSubmitLoading = false;
  }
}

onMounted(() => {
  state.isMounted = true;

  if (!isTouchDevice()) {
    focus();
  }
});

watch(
  () => stepper.current.value,
  async () => {
    window.scrollTo(0, 0);
    clearError();

    if (!isTouchDevice()) {
      await nextTick();
      focus();
    }
  }
);

init();
</script>

<template>
  <section class="bank-transfer-beneficiary-form-view">
    <InnerLayout
      :button-type="InnerLayoutButtonType.Back"
      :tip-title="tipTitle"
      :tip-description="tipDescription"
      @back="goBack"
    >
      <template #header>
        <UiProgressLine
          class="bank-transfer-beneficiary-form-view__progress"
          :progress="currentProgressStep.currentValue"
          :potential-progress="currentProgressStep.potentialValue"
        />
      </template>

      <form
        v-if="currentFormStep"
        :key="stepper.current.value"
        v-motion="internalMotion"
        class="bank-transfer-beneficiary-form-view__form"
        @submit.prevent="goNext"
      >
        <UiTitle class="bank-transfer-beneficiary-form-view__title" :level="1">
          {{ currentFormStep.title }}
        </UiTitle>
        <UiText class="bank-transfer-beneficiary-form-view__description" tag="p" size="md" muted>
          {{ currentFormStep.description }}
        </UiText>
        <UiInput
          :ref="onChangeInputRef"
          class="bank-transfer-beneficiary-form-view__input"
          :model-value="currentFormStep.value"
          :placeholder="currentFormStep.title"
          :mask="currentFormStep.mask"
          :error="state.error"
          :disabled="isInputDisabled"
          :uppercase="currentFormStep.step === BankTransferBeneficiaryFormViewStep.Iban"
          @update:model-value="currentFormStep.handle"
          @blur="currentFormStep.blur"
        />
        <div class="bank-transfer-beneficiary-form-view__footer">
          <UiText
            v-if="currentFormStep.optional"
            class="bank-transfer-beneficiary-form-view__note"
            size="sm"
            align="center"
            tag="p"
            muted
          >
            {{ t('createBeneficiary.nickName.isOptional', { field: currentFormStep.title }) }}
          </UiText>
          <div class="bank-transfer-beneficiary-form-view__buttons-wrapper">
            <UiButton
              v-if="currentFormStep.optional"
              class="bank-transfer-beneficiary-form-view__button"
              mod="secondary"
              @click="skip"
            >
              {{ skipText }}
            </UiButton>
            <UiButton
              class="bank-transfer-beneficiary-form-view__button"
              type="submit"
              :loading="isFormButtonLoading"
              :disabled="isFormButtonDisabled"
            >
              {{ t('buttons.continue') }}
            </UiButton>
          </div>
        </div>
      </form>
      <TransactionReview
        v-else
        v-motion="internalMotion"
        :title="t('createBeneficiary.review.title')"
        :details="details"
        :button-text="reviewButtonText"
        :loading="state.isSubmitLoading"
        :note="t('createBeneficiary.review.tip')"
        @submit="onSubmit"
      >
        <template #icon>
          <UiColoredCircle :text="getInitials(name)" :color="placeholderBackgroundColor(name)" />
        </template>
      </TransactionReview>
    </InnerLayout>
  </section>
</template>

<style scoped>
.bank-transfer-beneficiary-form-view {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.bank-transfer-beneficiary-form-view__progress {
  max-width: 120px;
}

.bank-transfer-beneficiary-form-view__form {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.bank-transfer-beneficiary-form-view__title {
  margin: 0 0 8px;
}

.bank-transfer-beneficiary-form-view__description {
  margin: 0 0 40px;
}

.bank-transfer-beneficiary-form-view__input {
  margin: 0 0 40px;
}

.bank-transfer-beneficiary-form-view__footer {
  margin: auto 0 0;
}

.bank-transfer-beneficiary-form-view__note {
  margin: 0 0 16px;
}

.bank-transfer-beneficiary-form-view__buttons-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: auto 0 0;
}

@media (max-width: 413px) {
  .bank-transfer-beneficiary-form-view__buttons-wrapper {
    flex-direction: column-reverse;
  }
}

.bank-transfer-beneficiary-form-view__button {
  width: calc(50% - 12px);
}

@media (max-width: 413px) {
  .bank-transfer-beneficiary-form-view__button {
    width: 100%;
  }
}

@media (max-width: 413px) {
  .bank-transfer-beneficiary-form-view__button:not(:last-child) {
    margin: 16px 0 0;
  }
}

.bank-transfer-beneficiary-form-view__button:first-child:last-child {
  width: 100%;
}
</style>
